import { Composer } from '@cortex/feature-resident-assistant/ui/web/components/Composer';
import { DemoDashboardLink } from '@cortex/feature-resident-assistant/ui/web/components/DemoDashboardLink';
import { TypingIndicator } from '@cortex/feature-resident-assistant/ui/web/components/TypingIndicator';
import {
  ApiError,
  type PmChatMessageItem,
  type PmChatPickerButton,
  type PmChatPickerItem,
  type PmChatTimelineItem,
  pmChatMessages,
  pmChatPublicStart,
  pmChatReset,
  pmChatSend,
  pmChatTap,
} from '@cortex/web-api/clients/web/api';
import { Wordmark } from '@cortex/web-api/ui/web/Wordmark';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

const POLL_MS = 1800;

type Status = 'starting' | 'ready' | 'unavailable';

export function PmChatScreen() {
  const [status, setStatus] = useState<Status>('starting');
  const [items, setItems] = useState<PmChatTimelineItem[]>([]);
  const [optimistic, setOptimistic] = useState<PmChatMessageItem[]>([]);
  const [waiting, setWaiting] = useState(false);
  const [networkBlip, setNetworkBlip] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const res = await pmChatMessages();
      const visible = [...res.timeline]
        .reverse()
        .filter((item) => item.type !== 'message' || !item.system);
      setItems(visible);
      setOptimistic([]);
      setNetworkBlip(false);
      if (
        visible.some((item) => item.type === 'message' && item.direction === 'outbound') ||
        visible.some((item) => item.type === 'picker')
      ) {
        setWaiting(false);
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        try {
          await pmChatPublicStart();
          setStatus('ready');
        } catch {
          setStatus('unavailable');
        }
        return;
      }
      setNetworkBlip(true);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function start() {
      try {
        await pmChatPublicStart();
        if (cancelled) return;
        setStatus('ready');
        await load();
      } catch {
        if (!cancelled) setStatus('unavailable');
      }
    }
    void start();
    return () => {
      cancelled = true;
    };
  }, [load]);

  useEffect(() => {
    if (status !== 'ready') return;
    const id = window.setInterval(() => void load(), POLL_MS);
    return () => window.clearInterval(id);
  }, [status, load]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  });

  async function handleSend(body: string) {
    const msg: PmChatMessageItem = {
      type: 'message',
      id: `optimistic-${Date.now()}`,
      direction: 'inbound',
      body,
      sentAt: new Date().toISOString(),
      deliveryStatus: 'received',
      actorDisplayName: 'Sandbox PM',
    };
    setOptimistic((prev) => [...prev, msg]);
    setWaiting(true);
    setError(null);
    try {
      await pmChatSend(body);
      await load();
    } catch (err) {
      setWaiting(false);
      setError(
        err instanceof ApiError && err.status === 429
          ? 'This public PM chat reached its message cap.'
          : "Couldn't send that message. Try again.",
      );
    }
  }

  async function handleTap(callbackData: string) {
    setError(null);
    try {
      await pmChatTap(callbackData);
      await load();
    } catch {
      setError("Couldn't apply that tap. The card may already be closed.");
    }
  }

  async function handleReset() {
    setError(null);
    try {
      await pmChatReset();
      setItems([]);
      setOptimistic([]);
      setWaiting(false);
      await load();
    } catch {
      setError("Couldn't reset the visible thread.");
    }
  }

  if (status === 'starting') {
    return (
      <Shell subtitle="pm sandbox">
        <div className="center-stack">
          <p className="field-help">opening PM chat…</p>
        </div>
      </Shell>
    );
  }
  if (status === 'unavailable') {
    return (
      <Shell subtitle="pm sandbox">
        <div className="center-stack">
          <div className="card" style={{ padding: 'var(--space-6)', maxWidth: 420 }}>
            <p className="field-error">PM Chat is only available in the sandbox environment.</p>
            <p className="field-help" style={{ marginTop: 'var(--space-2)' }}>
              Use the PM Chat tab from the demo dashboard after the sandbox API is deployed.
            </p>
          </div>
        </div>
      </Shell>
    );
  }

  const rendered = [...items, ...optimistic];

  return (
    <Shell subtitle="pm sandbox" onReset={handleReset}>
      <main className="app-main">
        <div
          ref={scrollRef}
          style={{
            flex: '1 1 auto',
            overflowY: 'auto',
            padding: 'var(--space-6)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-3)',
            background:
              'radial-gradient(circle at top left, rgba(16,132,108,.07), transparent 26rem), var(--paper)',
          }}
        >
          {rendered.length === 0 && !waiting && (
            <div
              style={{
                margin: 'auto',
                textAlign: 'center',
                color: 'var(--muted)',
                maxWidth: 460,
                fontFamily: 'var(--font-sans)',
                lineHeight: 'var(--lh-body)',
              }}
            >
              PM sandbox chat is ready. Try “create a task for a leaking sink at 74 Grove” or “send
              an NOE for 74 Grove unit 1 next Thursday 10–12 for inspection”.
            </div>
          )}
          {rendered.map((item) =>
            item.type === 'picker' ? (
              <PmPickerCard key={item.id} picker={item} onTap={handleTap} />
            ) : (
              <PmMessageBubble key={item.id} message={item} />
            ),
          )}
          {waiting && <TypingIndicator />}
          {networkBlip && <StatusPill>reconnecting…</StatusPill>}
          {error && <StatusPill>{error}</StatusPill>}
        </div>
        <Composer onSend={handleSend} placeholder="Message the PM agent…" disabled={waiting} />
      </main>
    </Shell>
  );
}

function Shell({
  children,
  subtitle,
  onReset,
}: { children: ReactNode; subtitle: string; onReset?: () => void }) {
  return (
    <div className="app">
      <header className="app-header">
        <img src="/acel-cortex-mark.svg" alt="" width={28} height={28} />
        <Wordmark size={18} />
        <span style={{ marginLeft: 'auto' }} className="label">
          {subtitle}
        </span>
        {onReset && (
          <button
            type="button"
            className="btn-secondary"
            onClick={onReset}
            style={{ padding: '8px 14px', fontSize: 12 }}
          >
            reset
          </button>
        )}
        <DemoDashboardLink />
      </header>
      {children}
    </div>
  );
}

function PmMessageBubble({ message }: { message: PmChatMessageItem }) {
  const isPm = message.direction === 'inbound';
  return (
    <div style={{ alignSelf: isPm ? 'flex-end' : 'flex-start', maxWidth: '82%' }}>
      <div
        style={{
          background: isPm ? 'var(--green)' : 'var(--paper-2)',
          color: isPm ? 'white' : 'var(--ink)',
          border: isPm ? '1px solid var(--green-deep)' : '1px solid var(--rule)',
          borderRadius: isPm ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
          padding: '10px 14px',
          fontFamily: 'var(--font-sans)',
          lineHeight: 'var(--lh-body)',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          boxShadow: 'var(--shadow-subtle)',
        }}
      >
        {message.body}
      </div>
      {message.actorDisplayName && isPm && (
        <div className="field-help" style={{ textAlign: 'right', fontSize: 11, marginTop: 3 }}>
          {message.actorDisplayName}
        </div>
      )}
    </div>
  );
}

function PmPickerCard({
  picker,
  onTap,
}: { picker: PmChatPickerItem; onTap: (callbackData: string) => void }) {
  const closed = picker.state !== 'awaiting';
  return (
    <div
      style={{
        alignSelf: 'flex-start',
        maxWidth: '88%',
        background: 'var(--paper)',
        border: '1px solid var(--rule)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-subtle)',
        padding: 'var(--space-3)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
      }}
    >
      <div className="label">{picker.approvalKind.replace(/^pm_/, '').replace(/_/g, ' ')}</div>
      <div
        style={{
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontFamily: 'var(--font-sans)',
          lineHeight: 'var(--lh-body)',
        }}
      >
        {picker.summary}
      </div>
      {picker.buttonRows.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
          {picker.buttonRows.map((row, idx) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: reconstructed static keyboard row
            <div key={idx} style={{ display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap' }}>
              {row.map((button) => (
                <PmPickerButton
                  key={button.callbackData}
                  button={button}
                  disabled={closed}
                  onTap={onTap}
                />
              ))}
            </div>
          ))}
        </div>
      )}
      <div className="field-help" style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>
        {pickerFooter(picker)}
      </div>
    </div>
  );
}

function PmPickerButton({
  button,
  disabled,
  onTap,
}: { button: PmChatPickerButton; disabled: boolean; onTap: (callbackData: string) => void }) {
  return (
    <button
      type="button"
      className={button.pressed ? 'btn-primary' : 'btn-secondary'}
      disabled={disabled || button.pressed}
      onClick={() => onTap(button.callbackData)}
      style={{
        borderRadius: 12,
        padding: '8px 10px',
        fontSize: 13,
        flex: '1 1 180px',
        whiteSpace: 'normal',
      }}
    >
      {button.pressed ? `✓ ${button.label}` : button.label}
    </button>
  );
}

function pickerFooter(picker: PmChatPickerItem): string {
  if (picker.state === 'awaiting') return 'awaiting tap';
  if (picker.decidedLabel) return `PM tapped: ${picker.decidedLabel}`;
  if (picker.state === 'superseded') return 'PM replied in chat instead';
  return picker.state;
}

function StatusPill({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        alignSelf: 'center',
        fontFamily: 'var(--font-mono)',
        fontSize: 'var(--fs-mono-sm)',
        color: 'var(--muted)',
        textTransform: 'uppercase',
        letterSpacing: 'var(--tracking-mono)',
      }}
    >
      {children}
    </div>
  );
}
